// Example sentences for the grammar terms (Axel 2026-10-21: every concept the Kielioppi names gets a couple of
// sentences showing what it is, when it is used and how to spot it). The term card (TermCard.tsx) shows each one with
// its English and Finnish and the concept's word(s) marked.
//
// Only Peterson's own sentences: `ex` is a bundle example id (wiki examples ex-*, his verified off-wiki sentences
// pt:*), found with `npm run hv -- grep` on the translator's chosen reading and each marked word checked with
// `npm run hv -- read` (the valyrian skill). `mark` lists the sentence's words to mark, as written there (space
// separated; empty when the concept has no word of its own, as in a yes–no question). Not every term has one: the
// sounds, the Finnish comparison terms and the parts of a word (stem, ending, class) are shown on the pages' tables;
// persoonaton and perifrastinen-passiivi have Peterson's sentence on the page itself; for the pluperfect no plain
// sentence of his was found. app/test/grammar-term-examples.test.ts checks every id and mark.

export interface TermExample {
  /** Bundle example id. */
  ex: string;
  /** The words to mark, as written in the sentence, space separated. */
  mark: string;
}

const e = (ex: string, mark: string): TermExample => ({ ex, mark });

export const TERM_EXAMPLES: Readonly<Record<string, readonly TermExample[]>> = {
  // nouns
  substantiivi: [e('ex-295193094c03', 'Vala ābre'), e('pt:cm-c-0008', 'Ābra muña')],
  suku: [e('ex-15de9915a9b0', 'Sylvie vala'), e('ex-76b7703b3ead', 'Sȳrior havon')],
  kuusuku: [e('ex-15de9915a9b0', 'Sylvie vala'), e('pt:cm-c-0035', 'Vali kirini')],
  aurinkosuku: [e('pt:cm-c-0025', 'Sȳz azantys'), e('ex-8612febc0c1a', 'Bykys Arraks')],
  maasuku: [e('ex-76b7703b3ead', 'Sȳrior havon'), e('ex-5a53cfc71ac1', 'Keson iōrion bōson')],
  vesisuku: [e('ex-c0bc63ffb2fb', 'Dārōñor ānogri'), e('ex-fd2f7bfc575e', 'Ñuhor qintir')],
  luku: [e('ex-539dd2489f31', 'Korze'), e('pt:cm-c-0035', 'Vali kirini issi')],
  yksikko: [e('ex-539dd2489f31', 'Korze'), e('ex-65a8999f9b5e', 'Ābra vale')],
  monikko: [e('pt:cm-c-0035', 'Vali'), e('ex-1f3f68ca1b7d', 'Taobi')],
  muutama: [e('ex-340c7cc989fb', 'gerpuni'), e('ex-c7292c764a88', 'Olvȳn')],
  kollektiivi: [e('ex-7963d3403004', 'jelmior'), e('ex-51af22aff14d', 'Dȳñer raqiror')],
  sija: [e('ex-129fb0aa3654', 'Embro tale'), e('ex-0c3a115caa51', 'ñuhā tembyrry')],
  nominatiivi: [e('ex-295193094c03', 'Vala'), e('ex-025fb4f1c97c', 'Pȳdes')],
  akkusatiivi: [e('ex-65a8999f9b5e', 'vale'), e('ex-129fb0aa3654', 'tale')],
  genetiivi: [e('ex-129fb0aa3654', 'Embro'), e('ex-4d542335439b', 'glaeso')],
  datiivi: [e('ex-3fa50b4ee9fa', 'īlot'), e('ex-afb4e01ff3d4', 'Ynot')],
  lokatiivi: [e('ex-346a5bdf3777', 'Ñuho prūmiā'), e('ex-0c3a115caa51', 'ñuhā tembyrry')],
  instrumentaali: [e('ex-9d0dcb2f6da3', 'Vēdroso'), e('ex-4b3a6c49b884', 'līrinoso')],
  komitatiivi: [e('ex-f5e4623aa8f6', 'Sūbroma'), e('ex-1786068063f0', 'Meraksome')],
  vokatiivi: [e('ex-bec327f6bf2e', 'azantys'), e('ex-08000e9e7986', 'Kātelissi')],
  elollisuus: [e('ex-488188c4e596', 'Sparos'), e('ex-2a39244230f1', 'Skoros')],
  ainesana: [e('ex-9718a5728d97', 'jūlor'), e('ex-c0bc63ffb2fb', 'ānogri')],

  // adjectives
  adjektiivi: [e('ex-15de9915a9b0', 'Sylvie'), e('ex-e89b8e3ea14e', 'byka')],
  mukautuminen: [e('pt:cm-c-0035', 'Vali kirini issi'), e('ex-0ff7f719b6b4', 'Geltī aōhe')],
  predikatiivi: [e('ex-539dd2489f31', 'sȳz'), e('ex-5a53cfc71ac1', 'bōson')],
  etuasema: [e('ex-15de9915a9b0', 'Sylvie'), e('pt:cm-c-0025', 'Sȳz')],
  jalkiasema: [e('ex-05b16fb26794', 'mandus'), e('ex-991b56eca6b7', 'ñuhys')],
  vertailuaste: [e('ex-23f825fba84a', 'rōvyktys'), e('ex-c63fa45474e2', 'sȳrpys')],
  positiivi: [e('ex-15de9915a9b0', 'Sylvie'), e('ex-5a53cfc71ac1', 'bōson')],
  ekvatiivi: [e('ex-051788fbace1', 'bōsāpa'), e('ex-c63fa45474e2', 'sȳrpys')],
  komparatiivi: [e('ex-23f825fba84a', 'rōvyktys'), e('ex-991b56eca6b7', 'Sylvikton')],
  superlatiivi: [e('ex-34b291c49825', 'pāsābarje karājē'), e('ex-c8933dfdc449', 'rōvājor')],
  adverbi: [e('ex-ce208431aad7', 'Zān'), e('ex-de8d6d693ca2', 'Drējī')],
  maarite: [e('ex-15de9915a9b0', 'Sylvie'), e('ex-129fb0aa3654', 'Embro')],
  paasana: [e('ex-15de9915a9b0', 'vala'), e('ex-129fb0aa3654', 'tale')],

  // pronouns
  pronomini: [e('ex-2713f106e290', 'Nyke avy'), e('ex-347c9dfd25d7', 'Ynot mirros')],
  persoona: [e('ex-1767d1db9a5f', 'iksā'), e('ex-de8d6d693ca2', 'iksan')],
  persoonapronomini: [e('ex-2713f106e290', 'Nyke avy'), e('ex-afb4e01ff3d4', 'Ynot')],
  refleksiivi: [e('ex-9e39921da927', 'Aōle'), e('ex-933b7d025829', 'aōle')],
  omistussana: [e('ex-d1ad0661c9e2', 'Ñuhe'), e('ex-3d068ff3d668', 'Aōhys')],
  omistuspronomini: [e('ex-2c309f55683b', 'Ñuhon'), e('ex-56af41a285be', 'Ñuhor')],
  demonstratiivi: [e('ex-231c1e04f319', 'Bisi'), e('ex-e89b8e3ea14e', 'Kesa')],
  'epamaarainen-pronomini': [e('ex-347c9dfd25d7', 'mirros'), e('ex-d9c06527ea50', 'Daorys')],

  // verbs
  verbi: [e('ex-295193094c03', 'urnes'), e('ex-b32b1bb8096a', 'sōptas')],
  persoonapaate: [e('ex-1767d1db9a5f', 'iksā'), e('ex-de8d6d693ca2', 'iksan')],
  aikamuoto: [e('ex-295193094c03', 'urnes'), e('ex-025fb4f1c97c', 'ūndas')],
  preesens: [e('ex-295193094c03', 'urnes'), e('ex-d1ad0661c9e2', 'jumban')],
  aoristi: [e('ex-f5e4623aa8f6', 'rhaenin'), e('ex-794bb2d3e74e', 'ȳdrassis')],
  futuuri: [e('ex-33b16c683c61', 'īlza'), e('ex-56af41a285be', 'gūrēnna')],
  imperfekti: [e('ex-22631e87052b', 'korīles'), e('ex-11675da963cd', 'bughīlen')],
  perfekti: [e('ex-025fb4f1c97c', 'ūndas'), e('ex-173a42dd6200', 'āmastā')],
  perfektivartalo: [e('ex-a1d29450428a', 'ēdrutan'), e('ex-5ecbda953758', 'Derēptan')],
  'mennyt-toistuva': [e('ex-4db8ced920f4', 'istin'), e('ex-44e4e0c0fa41', 'jenitis')],
  tapaluokka: [e('ex-0ff7f719b6b4', 'nādīnās'), e('ex-60f645959f82', 'epilū')],
  indikatiivi: [e('ex-65a8999f9b5e', 'urnes'), e('ex-025fb4f1c97c', 'ūndas')],
  subjunktiivi: [e('ex-60f645959f82', 'epilū'), e('ex-83c8f48f01f6', 'nagetos')],
  imperatiivi: [e('ex-0ff7f719b6b4', 'nādīnās'), e('ex-918d4a87ed37', 'idakōs')],
  aktiivi: [e('ex-295193094c03', 'urnes'), e('ex-2713f106e290', 'jūndan')],
  passiivi: [e('ex-94c617179928', 'pryjēliks'), e('ex-cc3d5caead9a', 'izūgiliks')],
  paaluokka: [e('ex-295193094c03', 'urnes'), e('ex-f87db368ecab', 'arghuks')],
  infinitiivi: [e('ex-aaf787c4ca15', 'jagon'), e('ex-4f19a97404bc', 'Vestragon')],
  partisiippi: [e('ex-10d381016267', 'Ājehikarior'), e('pt:gr-c-0043', 'iprattrossi')],
  transitiivinen: [e('ex-65a8999f9b5e', 'urnes'), e('ex-b32b1bb8096a', 'sōptas')],
  valenssi: [e('ex-3fa50b4ee9fa', 'Geron īlot arrīs'), e('ex-afb4e01ff3d4', 'Ynot mirrori tepās')],
  applikatiivi: [e('ex-2ea7ff9cde65', 'udēmēlā'), e('ex-4a026762a612', 'ilimēlza')],
  'lokatiivinen-applikatiivi': [e('ex-2ea7ff9cde65', 'dōrot udēmēlā'), e('ex-a018b2370bcc', 'Iēdro bēvumbagon')],
  'obliikvinen-applikatiivi': [e('ex-4a026762a612', 'dāri ilimēlza'), e('ex-af0a31299a55', 'parklon ilimatas')],
  valinepassiivi: [e('ex-cd132c88ff0f', 'anektos'), e('ex-d8de6484395c', 'anektotos')],
  etuliite: [e('ex-e4f10f1725a3', 'ospryjagon'), e('ex-32beba3ab860', 'Henujagon')],
  johdin: [e('ex-3ec6b2e6a768', 'vīlībāzma'), e('ex-55374aa794a5', 'nūmāzma')],
  johdos: [e('ex-3ec6b2e6a768', 'vīlībāzma'), e('ex-55374aa794a5', 'nūmāzma')],
  kausatiivi: [e('ex-ad259681318a', 'aerittas'), e('ex-41813fdaae64', 'kustittas')],
  eventatiivi: [e('ex-e304b2b88b0a', 'ūndetan'), e('ex-1ea3907dff27', 'ūndetoks')],

  // sentences
  subjekti: [e('ex-295193094c03', 'Vala'), e('ex-025fb4f1c97c', 'Pȳdes')],
  objekti: [e('ex-295193094c03', 'ābre'), e('ex-025fb4f1c97c', 'vale')],
  'epasuora-objekti': [e('ex-3fa50b4ee9fa', 'īlot'), e('ex-afb4e01ff3d4', 'Ynot')],
  sanajarjestys: [e('ex-22631e87052b', 'Dāria irūdi korīles'), e('ex-65a8999f9b5e', 'Ābra vale urnes')],
  sov: [e('ex-295193094c03', 'Vala ābre urnes'), e('ex-025fb4f1c97c', 'Pȳdes vale ūndas')],
  postpositio: [e('ex-7e165715356a', 'syt'), e('ex-0afdf4868d02', 'mijiot')],
  prepositio: [e('ex-486bcdbd9d8f', 'hen'), e('ex-7b6eb4f655d8', 'va')],
  adpositio: [e('ex-05e46a22882e', 'Hen'), e('ex-c899af670cbb', 'syt')],
  kysymyssana: [e('ex-488188c4e596', 'Sparos'), e('ex-ccac73de0543', 'Skorī')],
  rinnastus: [e('ex-2a8d86e9669b', 'se'), e('ex-ee655aa956a2', 'se')],
  'konjunktiivinen-pidennys': [e('ex-53dc6ef50613', 'nēdȳr'), e('ex-c454772797cc', 'riñār')],
  paalause: [e('ex-b2fa5a89f9cc', 'ērinis iā morghūlis'), e('ex-1790a2bdbb27', 'jaelan')],
  sivulause: [e('ex-b2fa5a89f9cc', 'Skorī dēmalȳti tymptir tymis'), e('ex-1790a2bdbb27', 'Ynoma ozmijiō')],
  relatiivilause: [e('ex-e304b2b88b0a', 'Ābre kustittas lue'), e('ex-bb0d13e9c7b7', 'Riña ubardus lua')],
  relatiiviadjektiivi: [e('ex-41813fdaae64', 'lua'), e('pt:gr-c-0031', 'luon')],
  relatiivipronomini: [e('pt:gr-c-0018', 'lī'), e('ex-a21911c5c6c3', 'līr')],
  'kylla-ei-kysymys': [e('ex-11be124ca5b8', ''), e('ex-4be8ec25a9bf', '')],
  'epasuora-kysymys': [e('ex-78b8157db7c6', 'lo glaesos'), e('ex-7f6091232d4c', 'Lo tolije zaldrīzāeksio istos')],
  sidesana: [e('ex-5fa8abacc07e', 'Yn'), e('ex-e7be03f5d758', 'iā')],
  taydennys: [e('ex-3fa50b4ee9fa', 'Geron īlot'), e('ex-65a8999f9b5e', 'vale')],
  maare: [e('ex-ce208431aad7', 'Zān inkot'), e('ex-0c3a115caa51', 'ñuhā tembyrry')],

  // words
  lukusana: [e('ex-ff94886202cb', 'Mēri ampā'), e('ex-3aa809b20f73', 'Lantēpsa')],
  jarjestysluku: [e('ex-930f99fa9678', 'Ēlio'), e('ex-1e0d42215f9e', 'Ēlior')],
};

/** The words of a sentence, each with whether it is marked (punctuation stays with the text between). */
export function markedPieces(hv: string, mark: string): { text: string; marked: boolean }[] {
  const want = new Set(
    mark
      .split(/\s+/)
      .filter(Boolean)
      .map((w) => w.normalize('NFC')),
  );
  const out: { text: string; marked: boolean }[] = [];
  const re = /[\p{L}\p{M}'’]+/gu;
  let at = 0;
  for (const m of hv.normalize('NFC').matchAll(re)) {
    if (m.index > at) out.push({ text: hv.normalize('NFC').slice(at, m.index), marked: false });
    out.push({ text: m[0], marked: want.has(m[0]) });
    at = m.index + m[0].length;
  }
  if (at < hv.length) out.push({ text: hv.normalize('NFC').slice(at), marked: false });
  return out;
}
